// Fonte única de paths do client (React Router). Todo `Link`, `Navigate`,
// `navigate()` e config de rota deve consumir daqui — evita string-divergence
// PT-BR/EN espalhada pelo app.
//
// Convenção: paths "estáticos" são valores literais; rotas com parâmetro têm
// duas formas — `xxx` é o template com `:param` (pra config do router) e
// `xxxTo(value)` é o helper de navegação que recebe o valor concreto.
//
// REST API (axios/RTK Query → /v1/...) NÃO vive aqui — esses são paths de back.
import type { Role } from '@/config/constants';

export const ROUTES = {
  // públicas
  login: '/entrar',
  signup: '/cadastro',
  inviteAccept: '/convite/:token',
  inviteAcceptTo: (token: string) => `/convite/${token}`,
  passwordReset: '/redefinir-senha/:token',
  passwordResetTo: (token: string) => `/redefinir-senha/${token}`,

  // institucionais (públicas, sem guard — exigidas na verificação Meta Tech Provider)
  terms: '/termos-de-uso',
  privacy: '/politica-de-privacidade',
  dataDeletion: '/exclusao-de-dados',

  // pós-login
  root: '/',
  dashboard: '/painel',
  onboarding: '/configuracao-inicial',
  config: '/configuracoes',

  inbox: '/conversas',
  inboxQueue: '/conversas/fila',
  inboxConversation: '/conversas/:conversationId',
  inboxConversationTo: (id: string) => `/conversas/${id}`,

  queue: '/fila',

  contacts: '/clientes',
  contactDetail: '/clientes/:contactId',
  contactDetailTo: (id: string) => `/clientes/${id}`,

  // CRM Clientes. Duas leituras da mesma carteira, e a aba ativa vive na URL
  // (/crm/:tab) para sobreviver ao F5 — o quadro aberto só existia no slice.
  // Tabs: quadros (funil configurável, default) | carteira (kanban de segmentos).
  crm: '/crm',
  crmTab: '/crm/:tab',
  crmTabTo: (tab: string) => `/crm/${tab}`,

  // PDV / atendimento presencial (RF-01..RF-08). O guichê é a landing do ATENDENTE.
  guiche: '/guiche',
  produtos: '/produtos',

  // Produção (RF-04). Única rota do papel PRODUCAO, e a landing dele.
  producao: '/producao',

  // Metas de venda (RF-17/18/19). Tela dedicada com 4 abas; a aba ativa vive na
  // URL (/metas/:tab), persiste no F5. Tabs: acompanhamento | meta-loja |
  // meta-vendedor | historico.
  metas: '/metas',
  metasTab: '/metas/:tab',
  metasTabTo: (tab: string) => `/metas/${tab}`,

  // Indicadores de atendimento (MET-01..MET-28). Painel próprio, e não uma aba de
  // /empresas: lá o seletor de loja NAVEGA para fora da página (storeDrilldownTo),
  // e o MET-26 exige que escolher a loja REFILTRE sem sair. `/indicadores` e não
  // `/relatorios` porque o módulo é analítico e interativo (drill-down, tempo real,
  // configuração), não um gerador de arquivos. A aba ativa vive na URL e persiste
  // no F5. Tabs: visao-geral | tempo-real | tempos | sla | equipe | funil | origem
  // | satisfacao | whatsapp | config.
  indicadores: '/indicadores',
  indicadoresTab: '/indicadores/:tab',
  indicadoresTabTo: (tab: string) => `/indicadores/${tab}`,

  campanhas: '/campanhas',
  campanhaDetail: '/campanhas/:id',
  campanhaDetailTo: (id: string) => `/campanhas/${id}`,
  // Passos do builder na URL. A campanha nasce como rascunho no back antes de o
  // builder abrir, então sempre há um :id — não existe rota "/campanhas/nova" que
  // pudesse colidir com /campanhas/:id. O passo na URL é o que faz o F5 no meio do
  // fluxo continuar de onde parou. Passos: loja | publico | estrategia | revisar.
  campanhaEditStep: '/campanhas/:id/editar/:step',
  campanhaEditStepTo: (id: string, step: string) => `/campanhas/${id}/editar/${step}`,

  // Painel de rede (NetworkPage). A aba ativa vive na URL (/empresas/:tab),
  // persiste no F5. Tabs de rede: visao-geral | precos | equipe | config.
  stores: '/empresas',
  storesTab: '/empresas/:tab',
  storesTabTo: (tab: string) => `/empresas/${tab}`,
  storesNew: '/empresas/nova',
  // Drill-down de uma loja específica (StoreDrilldownPage). Slug sequencial.
  // A aba do detalhe vive na URL: overview | pricing | team | settings.
  storeDrilldown: '/empresas/loja/:slug',
  storeDrilldownTo: (slug: string | number) => `/empresas/loja/${slug}`,
  storeDrilldownTab: '/empresas/loja/:slug/:storeTab',
  storeDrilldownTabTo: (slug: string | number, storeTab: string) =>
    `/empresas/loja/${slug}/${storeTab}`,
} as const;

/**
 * Rota inicial de cada papel (RF-01/RF-04). ATENDENTE e PRODUCAO não têm Dashboard
 * de vendas — caem direto na tela do próprio posto. Todo redirect "pra casa"
 * (login, RequireGuest, index e catch-all) passa por aqui: um papel novo só precisa
 * de uma linha.
 */
const LANDING_BY_ROLE: Partial<Record<Role, string>> = {
  ATENDENTE: ROUTES.guiche,
  PRODUCAO: ROUTES.producao,
};

export function landingRouteForRole(role: Role | null | undefined): string {
  return (role && LANDING_BY_ROLE[role]) ?? ROUTES.root;
}
